// Enquiry plumbing for the website forms: dynamic form config (titles,
// blurbs, dropdown lists) from the API + real submissions to POST /api/enquiries.
// Static fallbacks mirror the seeded values so pages still render if the API is down.
import {
  batchOptions,
  corporateProgrammes,
  interestedCourses,
  itCourseOptions,
  trainingModes,
  travelCourseOptions,
  travelProgrammes,
} from "@/lib/portal/enquiries";

import { API_URL } from "@/lib/apiUrl";
import { getSiteCourses } from "@/lib/courses";

const FETCH_OPTS: RequestInit =
  { cache: "no-store" };

export type EnquiryTypeKey = "general" | "travel" | "personality" | "it" | "corporate" | "ielts";

export interface PublicEnquiryType {
  key: EnquiryTypeKey;
  label: string;
  blurb: string | null;
  options: Record<string, string[] | string>;
}

export const staticEnquiryTypes: PublicEnquiryType[] = [
  {
    key: "general",
    label: "General Enquiry",
    blurb: "Tell us the area you are interested in and we will point you to the right program.",
    options: { interestedCourses, travelCourseOptions, itCourseOptions, corporateProgrammes },
  },
  {
    key: "travel",
    label: "Travel & Tourism",
    blurb: "IATA diploma and MVA certificate batches, with weekday and weekend options.",
    options: { programmes: travelProgrammes, batchOptions },
  },
  {
    key: "personality",
    label: "Personality Development",
    blurb: "Confidence, communication and grooming sessions for students and professionals.",
    options: { autoProgramme: "Personality Development", batchOptions },
  },
  {
    key: "it",
    label: "IT Courses",
    blurb: "Gen AI, DM & AI, AI & Cybersecurity and Claude Mastery programs.",
    options: { programmes: itCourseOptions, batchOptions },
  },
  {
    key: "corporate",
    label: "Corporate Training",
    blurb: "Custom training for your teams, at MVA or at your organisation.",
    options: { programmes: corporateProgrammes, trainingModes },
  },
  {
    key: "ielts",
    label: "IELTS",
    blurb: "Academic and General IELTS coaching with mock tests.",
    options: { autoProgramme: "IELTS", batchOptions },
  },
];

/**
 * Server components only. Titles and blurbs are fixed; the dropdown lists come from the live course
 * catalogue (courses + their course categories), so adding a course or category in the dashboard
 * updates every enquiry form. Falls back to the static mirror if the catalogue can't be loaded.
 */
export async function getEnquiryTypes(): Promise<PublicEnquiryType[]> {
  let courses: Awaited<ReturnType<typeof getSiteCourses>>;
  try {
    courses = await getSiteCourses();
  } catch {
    return staticEnquiryTypes;
  }
  if (courses.length === 0) return staticEnquiryTypes;

  // category name → course titles, in catalogue order
  const byCategory = new Map<string, string[]>();
  for (const c of courses) byCategory.set(c.category, [...(byCategory.get(c.category) ?? []), c.title]);
  const titles = (category: string, fallback: string[]) => byCategory.get(category) ?? fallback;
  const single = (category: string, fallback: string) => {
    const t = byCategory.get(category);
    return t && t.length === 1 ? t[0] : fallback;
  };

  // dashboard category order, but only categories that actually have a course (Corporate Training is always offered)
  let categories = [...byCategory.keys()];
  try {
    const res = await fetch(`${API_URL}/courses/categories`, FETCH_OPTS);
    if (res.ok) {
      const json = (await res.json()) as { data?: string[] };
      if (Array.isArray(json.data) && json.data.length > 0) categories = json.data.filter((c) => byCategory.has(c));
    }
  } catch {
    /* keep the course-derived list */
  }
  const generalCategories = categories.includes("Corporate Training") ? categories : [...categories, "Corporate Training"];
  const courseLists: Record<string, string[]> = {};
  for (const [cat, list] of byCategory) courseLists[`courses:${cat}`] = list;
  // per-course batch choices ("Weekdays" / "Weekends" as ticked in the dashboard; empty = no batch field)
  const batchLists: Record<string, string[]> = {};
  for (const c of courses) {
    batchLists[`batches:${c.title}`] = [
      ...(c.weekdayBatch === true ? [batchOptions[0]] : []),
      ...(c.weekendBatch === true ? [batchOptions[1]] : []),
    ];
  }

  return staticEnquiryTypes.map((t) => {
    switch (t.key) {
      case "general":
        return { ...t, options: { ...t.options, interestedCourses: generalCategories, ...courseLists } };
      case "travel":
        return { ...t, options: { ...t.options, programmes: titles("Travel & Tourism", travelProgrammes), ...batchLists } };
      case "it":
        return { ...t, options: { ...t.options, programmes: titles("IT Courses", itCourseOptions), ...batchLists } };
      case "personality":
        return { ...t, options: { ...t.options, autoProgramme: single("Personality Development", "Personality Development"), ...batchLists } };
      case "ielts":
        return { ...t, options: { ...t.options, autoProgramme: single("IELTS", "IELTS"), ...batchLists } };
      default:
        return t; // corporate: training themes, not catalogue courses
    }
  });
}

/**
 * Client-side submit. If the visitor happens to be signed in, the session
 * token rides along so the enquiry links to their account ("My Enquiries").
 * Returns the enquiry reference shown on the success state.
 */
export async function submitEnquiryApi(payload: Record<string, unknown>): Promise<string> {
  const { readSession } = await import("@/lib/portal/session");
  const token = readSession()?.token;
  let res: Response;
  try {
    res = await fetch(`${API_URL}/enquiries`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
    });
  } catch {
    throw new Error("Cannot reach the server. Check your connection and try again.");
  }
  const data = (await res.json().catch(() => ({}))) as { message?: string; data?: { reference?: string } };
  if (!res.ok) {
    throw new Error(data.message ?? "Could not submit your enquiry. Please try again.");
  }
  // Conversion events - fbq / gtag only exist once the visitor has accepted the matching cookie category,
  // so nothing is sent without consent. No personal data is passed along.
  const w = window as Window & { fbq?: (...a: unknown[]) => void; gtag?: (...a: unknown[]) => void };
  w.fbq?.("track", "Lead", { content_name: String(payload.kind ?? "enquiry") });
  w.gtag?.("event", "generate_lead", { form_type: String(payload.kind ?? "enquiry") });
  return data.data?.reference ?? "";
}
